import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Table, Modal, Spin, Card, message, Switch, Space, Popconfirm, Alert } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { TextArea } = Input;

export default function FAQManager() {
  const [form] = Form.useForm();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getFAQs();
      if (response.data.success) {
        setFaqs(response.data.faqs);
      }
    } catch (error) {
      message.error('خطا در بارگذاری سوالات متداول');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let response;
      if (editingId) {
        response = await adminAPI.updateFAQ(editingId, values);
      } else {
        response = await adminAPI.createFAQ(values);
      }
      if (response.data.success) {
        message.success('سوال با موفقیت ذخیره شد!');
        form.resetFields();
        setEditingId(null);
        setIsModalVisible(false);
        loadFAQs();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ذخیره سوال');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditingId(record._id);
    setIsModalVisible(true);
  };

  const handleToggle = async (record) => {
    try {
      const response = await adminAPI.toggleFAQ(record._id);
      if (response.data.success) {
        setFaqs((prev) => prev.map((f) => (f._id === record._id ? response.data.faq : f)));
      }
    } catch (error) {
      message.error('خطا در تغییر وضعیت سوال');
    }
  };

  const handleDelete = async (record) => {
    try {
      const response = await adminAPI.deleteFAQ(record._id);
      if (response.data.success) {
        message.success('سوال حذف شد');
        loadFAQs();
      }
    } catch (error) {
      message.error('خطا در حذف سوال');
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= faqs.length) return;
    const reordered = [...faqs];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setFaqs(reordered);
    try {
      await adminAPI.reorderFAQs(reordered.map((f) => f._id));
    } catch (error) {
      message.error('خطا در بازچینش سوالات');
      loadFAQs();
    }
  };

  const columns = [
    {
      title: 'ترتیب',
      width: 90,
      render: (_, record, index) => (
        <Space size={2}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => move(index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === faqs.length - 1}
            onClick={() => move(index, 1)}
          />
        </Space>
      )
    },
    {
      title: 'سوال',
      dataIndex: 'question'
    },
    {
      title: 'جواب',
      dataIndex: 'answer',
      ellipsis: true
    },
    {
      title: 'فعال',
      width: 90,
      render: (_, record) => (
        <Switch checked={record.enabled} onChange={() => handleToggle(record)} />
      )
    },
    {
      title: 'عمل',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            ویرایش
          </Button>
          <Popconfirm title="این سوال حذف شود؟" okText="بله" cancelText="خیر" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card title="مدیریت سوالات متداول (FAQ)">
      <Alert
        type="info"
        message="سوالاتی که در صفحه‌ی /faq به کاربران نمایش داده می‌شوند. با کلید فعال/غیرفعال، بازچینش ترتیب و ویرایش، این صفحه از همین‌جا کنترل می‌شود."
        style={{ marginBottom: 16 }}
      />

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          form.resetFields();
          setEditingId(null);
          setIsModalVisible(true);
        }}
        style={{ marginBottom: '16px' }}
      >
        سوال جدید
      </Button>

      <Spin spinning={loading}>
        <Table columns={columns} dataSource={faqs} rowKey="_id" pagination={false} />
      </Spin>

      <Modal
        title={editingId ? 'ویرایش سوال' : 'سوال جدید'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ enabled: true }}>
          <Form.Item name="question" label="سوال" rules={[{ required: true, message: 'سوال الزامی است' }]}>
            <Input placeholder="مثال: چقدر طول می‌کشد تا دعوت‌نامه‌ام برسد؟" />
          </Form.Item>

          <Form.Item name="answer" label="جواب" rules={[{ required: true, message: 'جواب الزامی است' }]}>
            <TextArea rows={4} placeholder="متن پاسخ..." />
          </Form.Item>

          <Form.Item name="enabled" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              ذخیره
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
